import React, { useState } from 'react';

// Shared across every "grau de parentesco" field (new-resident registration, unit-assignment
// editing, public self-registration) so the option list and the "Outro" free-text toggle
// logic live in exactly one place instead of being reimplemented per call site.

export const PARENTESCO_OPTIONS = [
  { value: 'filho_a', label: 'Filho(a)' },
  { value: 'pai', label: 'Pai' },
  { value: 'mae', label: 'Mãe' },
  { value: 'irmao_a', label: 'Irmão(a)' },
  { value: 'esposa', label: 'Esposa' },
  { value: 'marido', label: 'Marido' },
  { value: 'tio_a', label: 'Tio(a)' },
  { value: 'sogro_a', label: 'Sogro(a)' },
  { value: 'cunhado_a', label: 'Cunhado(a)' },
];

const CUSTOM_SENTINEL = '__outro__';

/** Known label for a stored parentesco value, or the raw value itself for free-text ("Outro") entries. */
export function getParentescoLabel(value: string): string {
  return PARENTESCO_OPTIONS.find(o => o.value === value)?.label || value;
}

interface ParentescoOptionsSelectProps {
  /** The actual stored value — one of PARENTESCO_OPTIONS' values, arbitrary free text
   * (from "Outro"), or empty. There's no separate "is this custom" flag: any non-empty
   * value that doesn't match a known option is treated as a custom "Outro" entry. */
  value: string;
  onChange: (value: string) => void;
  className: string;
  disabled?: boolean;
}

/** Renders the parentesco <select> and, when "Outro" is active, a free-text input right
 * below it. No label/wrapper — callers keep their own layout since it differs per modal. */
export const ParentescoOptionsSelect: React.FC<ParentescoOptionsSelectProps> = ({ value, onChange, className, disabled }) => {
  const isKnownOption = PARENTESCO_OPTIONS.some(o => o.value === value);
  // Tracks "Outro" explicitly picked in a render where `value` is still empty (user hasn't
  // typed yet) — without this the select would snap back to the placeholder the instant
  // they choose "Outro", since an empty value doesn't look custom on its own.
  const [manualCustom, setManualCustom] = useState(false);
  const showCustomInput = manualCustom || (!!value && !isKnownOption);
  const selectValue = showCustomInput ? CUSTOM_SENTINEL : value;

  return (
    <>
      <select
        value={selectValue}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value === CUSTOM_SENTINEL) {
            setManualCustom(true);
            onChange('');
          } else {
            setManualCustom(false);
            onChange(e.target.value);
          }
        }}
        className={className}
      >
        <option value="">Selecionar...</option>
        {PARENTESCO_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
        <option value={CUSTOM_SENTINEL}>Outro</option>
      </select>
      {showCustomInput && (
        <input
          type="text"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Especifique o grau de parentesco"
          className={className}
        />
      )}
    </>
  );
};
